<template>
  <div class="q-gutter-y-lg q-mx-auto">
    <q-card flat bordered>
      <q-card-section>
        <div class="text-h5">Create a Regional Report</div>
        <div :class="$q.dark.isActive ? 'text-grey-5' : 'text-grey-7'">
          Regional Reports allow you to analyze properties within a specific geographic area. Start
          by defining the region by entering a neighborhood, selecting a zip code, or by drawing a
          custom boundary on the map. Once a region has been defined, you can filter the properties
          based on key characteristics such as <strong>Year Built</strong>,
          <strong>Number of Units</strong>, or <strong>Number of Stories</strong>.
        </div>

        <market-report-google-map
          v-if="!loading"
          id="intercom-market-report-google-map"
          :filtered-properties="filteredProperties"
          :filters="reportFilters"
          :loading="recomputing"
          enable-edit
          @update-coordinates="updateCoordinates"
        />
        <q-skeleton v-else class="google-map" animation="blink" />

        <div class="q-my-lg">
          <div class="text-h6 q-mb-sm">Filter the Region by Property Characteristics</div>
          <template v-for="(f, idx) in filters" :key="idx">
            <min-max-filter
              v-if="f.filter.key === 'year_built'"
              :min="f.minValue"
              :max="f.maxValue"
              raw-numbers
              no-apply-btn
              :title="f.filter.label"
              class="q-mr-sm"
              @hide="recomputeProperties"
              @update-range="handleRangeUpdate($event, f)"
            >
              {{ f.filter.label }}
            </min-max-filter>
            <min-max-filter
              v-else
              :min="f.minValue"
              :max="f.maxValue"
              no-apply-btn
              :title="f.filter.label"
              class="q-mr-sm"
              @hide="recomputeProperties"
              @update-range="handleRangeUpdate($event, f)"
            >
              {{ f.filter.label }}
            </min-max-filter>
          </template>
        </div>
      </q-card-section>
      <q-card-section v-if="filteredProperties.length === 1000">
        <warning-banner>
          Your market report exceeds the <b>1,000</b> property limit. A random sample of properties
          within your filters is currently displayed. Refine your filters to view all data.
        </warning-banner>
      </q-card-section>
      <q-card-section>
        <property-compare-unit-type-metrics
          v-if="!loading"
          id="intercom-property-compare-unit-type-availability"
          :filtered-properties="filteredProperties"
          :date-range="dateRange"
        />
        <div v-else class="row q-col-gutter-sm">
          <div
            v-for="index in 4"
            :key="index"
            class="col-3"
            :class="{ 'col-6': $q.screen.lt.md, 'col-12': $q.screen.lt.md }"
          >
            <q-card flat bordered>
              <q-card-section class="row justify-between items-center">
                <div class="text-h6">
                  <q-skeleton animation="blink" style="width: 100px" />
                </div>
                <div class="trailing-text">
                  <q-skeleton animation="blink" style="width: 125px" />
                </div>
              </q-card-section>
              <q-separator />
              <q-card-section class="row q-col-gutter-x-md q-col-gutter-y-xs">
                <div
                  v-for="ins in 6"
                  :key="ins"
                  class="text-caption col-12 justify-between flex"
                  :class="{ 'col-6': $q.screen.lt.md }"
                >
                  <span class="text-grey-7">
                    <q-skeleton animation="blink" type="text" style="width: 150px" />
                  </span>
                  <span class="q-ml-xs highlighted-number">
                    <q-skeleton animation="blink" type="text" style="width: 50px" />
                  </span>
                </div>
              </q-card-section>
            </q-card>
          </div>
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>

<script lang="ts">
import { type PropType } from 'vue'
import { addDays } from 'date-fns'
import { getUnitsByFilter } from 'common'
import type {
  MarketFilters,
  MarketReportSubscriptionAttributes,
  SimplePropertyDetailsResponse
} from 'common'
import PropertyCompareUnitTypeMetrics from '../PropertyReports/PropertyCompareUnitTypeMetrics.vue'
import MarketReportGoogleMap from './MarketReportGoogleMap.vue'
import { getLatestPricesByFilter, getUnitTypes } from 'common/utils'
import { unitTypeColor } from '@/utils'
import { useRentsourceStore } from '@/stores/rentsource'
import MinMaxFilter from '@/components/PropertyReports/MinMaxFilter.vue'
import WarningBanner from '@/components/WarningBanner.vue'

type SelectedFilter = {
  filter: {
    label: string
    key: string
    type: string
    options: any[]
  }
  values: (string | number)[]
  minValue: null | number
  maxValue: null | number
}

export default {
  name: 'EnterMarkets',
  components: {
    WarningBanner,
    MinMaxFilter,
    PropertyCompareUnitTypeMetrics,
    MarketReportGoogleMap
  },
  props: {
    report: {
      type: Object as PropType<MarketReportSubscriptionAttributes>,
      required: true
    }
  },
  emits: ['update-report'],
  data() {
    return {
      properties: null as null | SimplePropertyDetailsResponse[],
      listingsSelection: [
        { label: 'Active', value: 'active' },
        { label: 'Closed', value: 'closed' }
      ] as {
        label: string
        value: 'all' | 'active' | 'closed'
      }[],
      dateRange: { from: addDays(new Date(), -30), to: new Date() } as {
        from: Date
        to: Date
      },
      filters: [
        {
          filter: {
            label: 'Year Built',
            key: 'year_built',
            type: 'range',
            options: []
          },
          values: [],
          minValue: null,
          maxValue: null
        },
        {
          filter: {
            label: '# Units',
            key: 'number_units',
            type: 'range',
            options: []
          },
          values: [],
          minValue: null,
          maxValue: null
        },
        {
          filter: {
            label: 'Stories',
            key: 'number_stories',
            type: 'range',
            options: []
          },
          values: [],
          minValue: null,
          maxValue: null
        }
      ] as SelectedFilter[],
      coordinates: [] as number[][][][],
      recomputing: false
    }
  },
  computed: {
    reportFilters() {
      const yearBuilt = this.filters.find((_) => _.filter.key === 'year_built')!
      const numberUnits = this.filters.find((_) => _.filter.key === 'number_units')!
      const numberStories = this.filters.find((_) => _.filter.key === 'number_stories')!
      return {
        coordinates: this.coordinates,
        year_built: {
          min: yearBuilt.minValue,
          max: yearBuilt.maxValue
        },
        number_units: {
          min: numberUnits.minValue,
          max: numberUnits.maxValue
        },
        number_stories: {
          min: numberStories.minValue,
          max: numberStories.maxValue
        }
      } as MarketFilters
    },
    loading() {
      return this.report.id !== -1 && this.properties === null
    },
    unitTypeColors() {
      return this.unitTypeOptions.map((_) => unitTypeColor(_.value.bed))
    },
    unitTypeOptions() {
      return getUnitTypes(this.properties || []).map((_) => ({
        label: this.unitTypeLabel(_),
        value: _
      }))
    },
    filteredProperties() {
      return (this.properties || []).flatMap((prop) => {
        return {
          prop,
          units: getUnitsByFilter(
            prop,
            getLatestPricesByFilter(prop, this.dateRange.from, this.dateRange.to),
            this.dateRange.to,
            this.listingsSelection.length === 1 ? this.listingsSelection[0].value : 'all'
          )
        }
      })
    }
  },
  created() {
    this.coordinates = this.report.filters.coordinates
    const { year_built, number_units, number_stories } = this.report.filters
    this.filters.forEach((filter) => {
      if (filter.filter.key === 'year_built') {
        filter.minValue = year_built.min
        filter.maxValue = year_built.max
      } else if (filter.filter.key === 'number_units') {
        filter.minValue = number_units.min
        filter.maxValue = number_units.max
      } else if (filter.filter.key === 'number_stories') {
        filter.minValue = number_stories.min
        filter.maxValue = number_stories.max
      }
    })
    this.recomputeProperties()
  },
  methods: {
    async recomputeProperties() {
      if (this.coordinates.length) {
        this.recomputing = true
        await useRentsourceStore()
          .marketSearch(this.reportFilters)
          .then((data) => {
            this.properties = data.properties
          })
          .finally(() => {
            this.recomputing = false
          })
      } else {
        this.properties = []
      }
      this.$emit('update-report', {
        id: this.report.id,
        name: this.report.name,
        filters: this.reportFilters,
        user_id: this.report.user_id,
        created_on: this.report.created_on,
        modified_on: this.report.modified_on,
        deleted_on: this.report.deleted_on
      })
    },
    unitTypeLabel(unitType: { bed: number | null; bath: number | null }) {
      if (unitType.bed === null && unitType.bath === null) {
        return 'All Unit Types'
      }
      // We know that beds and baths are set together, one cannot be null without the other
      return `${unitType.bed} Bed ${unitType.bath} Bath`
    },
    updateCoordinates(coordinates: number[][][][]) {
      this.coordinates = coordinates
      this.recomputeProperties()
    },
    handleRangeUpdate(range: { min: number | null; max: number | null }, filter: SelectedFilter) {
      filter.minValue = range.min
      filter.maxValue = range.max
    }
  }
}
</script>
<style scoped>
.market-report {
  max-width: 1600px;
}
.google-map {
  width: 100%;
  min-height: 300px;
  height: calc(100vh / 4);
}
</style>
